// @flow
import React, { useEffect, useReducer, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
//actions
import {
    authReset,
    authLogin,
    authSSO,
    userAuthorizeDevice,
    userReset,
    useNotification,
    platformTenantDetails,
    publicGetTenantFromEmail,
} from '@ovrture/react-redux';
import { loginValidation } from '../../components/form/validation';
// components
import AccountLayout from '../account/AccountLayout';
import { FormInput } from '../../components/form/input/';
import ButtonElement from '../../components/button/ButtonElement';
import { ConfigReducer, configState } from '../../reducers/ConfigReducer';
import { RecaptchaProvider, ReCaptchaVerify } from '../../recaptcha';
import { convertIntoBase64 } from '../../utility';
import { useModalContext } from '../../contexts/ModalContext';
import { modalActions } from '../../reducers/actions';
import { useNotificationContex } from '../../contexts/NotificationContext';
import { SystemError } from '../../components/SystemError';
import service from '../../constants/service.constant';
import { COMMON_PATH, PLATFORM_ADMIN_PATH, RedirectTo, SYSTEM_ADMIN_PATH } from '../../routes/role/path';
import { getCurrentHost, getDeviceId, setDeviceId } from '../../helpers/retainHistory';
import MFA from './MFA';
import { clearFilterStore } from '../../helpers/dataFiltering';

const Login = () => {
    const [config] = useReducer(ConfigReducer, configState);
    const dispatch = useDispatch();
    const { modalDispatch } = useModalContext();
    const { notificationDispatch } = useNotificationContex();
    const { Toast, setAutoClose } = useNotification();
    const [ssoErrors, setSsoErrors] = useState('');
    const [mfaEnforced, setMfaEnforced] = useState(null);
    const [disabledBtn, setDisabledBtn] = useState(false);
    const urlParams = new URLSearchParams(window.location.search);

    useEffect(() => {
        clearFilterStore();
        validateDevice();
    }, []);

    const resetState = () => {
        dispatch(authReset());
        dispatch(userReset());
    };

    const { loading, user, error, lockedOut, authorizeDevice, emailSent, tenantDetails } = useSelector((state) => ({
        loading: state?.auth?.loading,
        user: state?.auth?.user,
        error: state?.auth?.error,
        lockedOut: state?.auth?.lockedOut,
        authorizeDevice: state?.user?.authorizeDevice,
        emailSent: state?.general?.emailSent,
        tenantDetails: state?.platformKey?.tenantDetails,
    }));
    const {
        register,
        handleSubmit,
        setError,
        formState: { errors },
    } = useForm({
        resolver: loginValidation,
        defaultValues: {
            username: urlParams?.get('email')?.replace(' ', '+'),
        },
    });

    const onSubmit = (formData) => {
        let deviceId = getDeviceId(formData['username']);
        setDisabledBtn(true);
        ReCaptchaVerify(tenantDetails?.data?.recaptchaSiteKey, 'login_submit', function (token) {
            dispatch(
                authLogin({
                    username: formData['username'],
                    password: convertIntoBase64(formData['password']),
                    recaptchaResponse: token,
                    deviceId: deviceId,
                })
            );
        });
    };

    useEffect(() => {
        if (tenantDetails?.code === service.success) {
            RecaptchaProvider('recaptcha_key', tenantDetails?.data?.recaptchaSiteKey, function () {});
        }
        if (tenantDetails?.data?.id > -1) {
            localStorage.setItem('TenantOrgId', tenantDetails?.data?.id);
        }
    }, [tenantDetails]);

    useEffect(() => {
        if (error) {
            setDisabledBtn(false);
            if (error?.mfaToken) {
                setMfaEnforced(error);
            } else if (error === 'Email is sent to authorize this device.') {
                setAutoClose(false);
                Toast.success({
                    title: 'Success',
                    description: 'Token/link sent; please check your email to authorize device.',
                    button: 'Close',
                });
                resetState();
                return null;
            } else {
                const errors = [
                    { password: error === 'Unauthorized' ? 'Invalid credentials.' : error },
                    { username: '' },
                ];
                SystemError(errors, setError, notificationDispatch);
                resetState();
            }
        }
        if (ssoErrors) {
            SystemError([{ username: ssoErrors }, { password: '' }], setError, notificationDispatch);
            setSsoErrors('');
        }
    }, [error, ssoErrors]);

    useEffect(() => {
        (async function () {
            try {
                if (lockedOut) {
                    modalDispatch({
                        type: modalActions.OPEN_MODAL,
                        payload: {
                            modalVariant: 'danger',
                            modalDataKey: ['platformAdmin', 'accountLocked'],
                            data: {
                                time: new Date(lockedOut),
                            },
                        },
                    });
                    resetState();
                } else {
                    let redirectTo = PLATFORM_ADMIN_PATH.TENANT_SYSTEMS;
                    if (user.roleId === 1 || user.roleId === 10) redirectTo = SYSTEM_ADMIN_PATH.USERS;
                    if (user.roleId === 2 || user.roleId === 5 || user.roleId === 6)
                        redirectTo = COMMON_PATH.REVIEW_AND_APPROVAL;
                    if (user.roleId === 3 || user.roleId === 4 || user.roleId === 7)
                        redirectTo = COMMON_PATH.RECORD_PORTFOLIO;
                    window.location.href = RedirectTo(redirectTo);
                }
            } catch (errorVal) {
                // throw new Error();
            }
        })();
    }, [user, lockedOut]);
    const ssoAuthentication = () => {
        let url = new URL(window.location.href);
        let ssoToken = url.searchParams.get('token');
        let ssoDeviceId = url.searchParams.get('deviceId');
        let ssoEmail = url.searchParams.get('email');
        if (ssoToken) {
            dispatch(authSSO(ssoToken));
        }
        let ssoError = url.searchParams.get('error');
        if (ssoError) setSsoErrors(ssoError);
    };

    useEffect(() => {
        localStorage.removeItem('apiGatewayUrl');
        localStorage.removeItem('CurrentFiscalYear');
        ssoAuthentication();
        dispatch(platformTenantDetails({ serverName: getCurrentHost() }));
    }, []);

    useEffect(() => {
        if (authorizeDevice) {
            if (authorizeDevice.code === service.accepted) {
                setDeviceId(authorizeDevice?.data?.deviceId, authorizeDevice?.data?.email);
                resetState();
                setTimeout(() => {
                    window.location.href = RedirectTo('/public/login');
                }, 3000);
            } else {
                setAutoClose(false);
                Toast.danger({
                    title: 'Error',
                    description: 'Device authentication token/link has expired; please try again.',
                    button: 'Close',
                });
                resetState();
            }
        }
    }, [authorizeDevice]);

    useEffect(() => {
        if (emailSent) {
            setAutoClose(2000);
            Toast.success({
                title: 'Success',
                description: 'Token/link sent; please check your email to procced.',
            });
            resetState();
        }
    }, [emailSent]);

    const validateDevice = () => {
        var url = new URL(window.location.href);
        var userToken = url.searchParams.get('userToken');
        var userId = url.searchParams.get('userId');
        if (userToken && userId) {
            dispatch(
                userAuthorizeDevice({
                    userToken: userToken,
                    userId: userId,
                })
            );
        }
    };

    return (
        <>
            {tenantDetails && (
                <AccountLayout>
                    {mfaEnforced ? (
                        <MFA mfaEnforced={mfaEnforced} />
                    ) : (
                        <>
                            <div className="text-center w-75 m-auto">
                                <p className="mb-3 pb-1 text-secondary text-opacity-75 login-text">
                                    Sign In with your Organization ID or your Ovrture Credentials.
                                </p>
                            </div>
                            <div className="text-center mb-3">
                                <ButtonElement
                                    name="Sign In with Organization ID"
                                    type="button"
                                    variant="primary w-100 sso-login-button"
                                    className={tenantDetails?.data?.ssoLoginURL === null && 'disabled'}
                                    onClick={() => (window.location.href = tenantDetails?.data?.ssoLoginURL)}
                                />
                            </div>
                            <div className="login-or text-center mt-4 mb-1">
                                <div className="border-bottom"></div>
                                <span className="text-secondary">or</span>
                            </div>
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <FormInput
                                    className="mb-1"
                                    type="text"
                                    id="username"
                                    label="Username/Email"
                                    errors={errors}
                                    register={register}
                                />
                                <div className="d-inline-block w-100">
                                    <FormInput
                                        type="password"
                                        id="password"
                                        label="Password"
                                        className="mb-0"
                                        errors={errors}
                                        register={register}
                                    />
                                    <ButtonElement
                                        name="Forgot your password?"
                                        className="float-end btn-link p-0 mb-1 forgot-password position-relative link-muted shadow-none"
                                        type="button"
                                        as="link"
                                        to="/public/forgot-password"
                                    />
                                </div>
                                <div className="mt-3 mb-0 text-center">
                                    <ButtonElement
                                        name="Sign In with Credentials"
                                        variant="primary"
                                        type="submit"
                                        className="w-100 login-button"
                                        disabled={disabledBtn}
                                        loading={loading}
                                    />
                                </div>
                            </form>
                        </>
                    )}
                </AccountLayout>
            )}
        </>
    );
};

export default Login;
